// Shared non-destructive crop / "zoom" helpers for the photo editor and the clip
// color studio. A crop is a normalized rect {x,y,w,h} in 0..1 of the SOURCE
// frame, optionally carrying flip/straighten geometry (rotate, flipH, flipV).
// The same rect drives the live GradedCanvas preview (getCrop) and the ffmpeg
// bake (backend cropFilter), so preview == export everywhere.

export const clampN = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// A small inset default box, used when seeding a free crop with no aspect lock.
export const DEFAULT_CROP_RECT = { x: 0.08, y: 0.08, w: 0.84, h: 0.84 };

// Reframe presets. `r` is output width/height; `source` locks the box to the
// ORIGINAL frame's aspect (zoom/reframe without changing shape — resolved from
// srcAR at runtime), `free` is any-shape, the rest lock to that ratio while you
// drag a corner. Shared by the photo editor, clip studio and frame grabber so
// reframing feels the same everywhere.
export const CROP_ASPECTS = [
  { id: 'source', label: 'Original', r: 'src' },
  { id: 'free', label: 'Free', r: null },
  { id: '3:2', label: '3:2', r: 3 / 2 },
  { id: '16:9', label: '16:9', r: 16 / 9 },
  { id: '4:5', label: '4:5', r: 4 / 5 },
  { id: '1:1', label: '1:1', r: 1 },
  { id: '9:16', label: '9:16', r: 9 / 16 },
];

// Centered crop rect {x,y,w,h} for a target output AR inside a source of `srcAR`.
export function rectForAR(targetAR, scale, srcAR) {
  if (!targetAR || !srcAR) return null;
  const r = targetAR / srcAR;
  const maxW = r >= 1 ? 1 : r;
  const maxH = r >= 1 ? 1 / r : 1;
  const w = clampN(scale, 0.1, 1) * maxW;
  const h = clampN(scale, 0.1, 1) * maxH;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

// Current zoom scale (0..1] of a rect = its size relative to the largest centered
// box of the SAME normalized proportion that fits the frame. 1 = widest (fills a
// frame dimension), smaller = tighter / more zoomed in. Full frame → 1.
export function rectScale(rect) {
  if (!rect || !(rect.w > 0) || !(rect.h > 0)) return 1;
  const p = rect.w / rect.h;
  const W = p >= 1 ? 1 : p;
  return clampN(rect.w / W, 0.05, 1);
}

// Zoom multiplier (1× = widest) ⇄ scale (0..1]. Used to drive the zoom slider.
export const zoomFromScale = (scale) => 1 / clampN(scale, 0.05, 1);
export const scaleFromZoom = (zoom) => clampN(1 / Math.max(1, Number(zoom) || 1), 0.1, 1);

// Resize a rect to `scale` (0..1] around its current center, preserving its
// normalized proportion (so the pixel aspect ratio is unchanged), then clamp
// inside the frame. When there's no rect yet we zoom from the full frame.
export function rectAtScale(rect, scale) {
  const r = rect && rect.w > 0 ? rect : { x: 0, y: 0, w: 1, h: 1 };
  const p = r.w / r.h;
  const W = p >= 1 ? 1 : p;
  const H = p >= 1 ? 1 / p : 1;
  const s = clampN(scale, 0.1, 1);
  const w = s * W;
  const h = s * H;
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const x = clampN(cx - w / 2, 0, 1 - w);
  const y = clampN(cy - h / 2, 0, 1 - h);
  return { x, y, w, h };
}

// ---- natural-language reframing --------------------------------------------
// Output pixel aspect ratio (w/h) for a named reframe shape. `source`/unknown
// returns null (keep the frame's current shape).
export function aspectRatioFromId(id) {
  if (!id || id === 'source' || id === 'free') return null;
  const a = CROP_ASPECTS.find((c) => c.id === id);
  return a && typeof a.r === 'number' ? a.r : null;
}

// Largest zoom we'll honor before a crop turns to mush, by source. RED/RAW
// negatives are 6-8K so a 3x punch-in still resolves; pre-graded MP4 is ~1080p,
// where past ~1.6x detail falls apart — so we clamp (and the caller can warn).
export function maxZoomForSource(redSource) {
  return redSource ? 3.0 : 1.6;
}

// Convert a model/UI framing intent {cx,cy,rx,ry,zoom,aspect} into a normalized
// crop rect, composing over any crop the frame already carries. `displayedAR` is
// the pixel aspect of the image the framing was chosen against (the current,
// possibly-cropped, display). Returns { crop, zoom, clamped }.
export function framingToCrop(framing, { displayedAR, existingCrop = null, redSource = false } = {}) {
  if (!framing || !displayedAR) return { crop: existingCrop, zoom: 1, clamped: false };
  const cap = maxZoomForSource(redSource);
  const reqZoom = Number(framing.zoom) > 0 ? Number(framing.zoom) : 1.6;
  const zoom = Math.max(1, Math.min(cap, reqZoom));
  const clamped = reqZoom > cap + 1e-6;

  const ar = aspectRatioFromId(framing.aspect);
  const targetAR = ar || displayedAR; // desired output pixel AR
  const scale = scaleFromZoom(zoom);

  let r = rectForAR(targetAR, scale, displayedAR) || { x: 0, y: 0, w: 1, h: 1 };
  const cx = clampN(Number(framing.cx) || 0.5, 0, 1);
  const cy = clampN(Number(framing.cy) || 0.5, 0, 1);
  const x = clampN(cx - r.w / 2, 0, 1 - r.w);
  const y = clampN(cy - r.h / 2, 0, 1 - r.h);
  r = { x, y, w: r.w, h: r.h };

  const c = existingCrop && existingCrop.w > 0 ? existingCrop : null;
  const crop = c
    ? { x: c.x + r.x * c.w, y: c.y + r.y * c.h, w: r.w * c.w, h: r.h * c.h }
    : r;
  return { crop: normalizeCrop(crop), zoom, clamped };
}

// Collapse a full-frame rect with no geometry to null (== "no crop"), so we
// never persist a no-op reframe.
export function normalizeCrop(rect) {
  if (!rect || !(rect.w > 0) || !(rect.h > 0)) return null;
  const full = rect.x <= 5e-4 && rect.y <= 5e-4 && rect.w >= 0.9995 && rect.h >= 0.9995;
  if (full && !rect.rotate && !rect.flipH && !rect.flipV) return null;
  return rect;
}

// ---- animated reframe ("keyframed zoom") -----------------------------------
// A reframe animation is a list of [{ id, t, rect }] where `rect` is a plain
// normalized {x,y,w,h} window (no geometry — flip/straighten live on the base
// crop and apply uniformly). All keys share the FIRST key's proportion so the
// rendered output has constant dimensions; the studio enforces that on edit.

export function sortReframeKeys(keys) {
  return [...(keys || [])].filter((k) => k && k.rect && k.rect.w > 0).sort((a, b) => (a.t || 0) - (b.t || 0));
}

// True when the keys actually move the framing (≥2 keys, or a single non-full
// window). One full-frame key is a no-op.
export function reframeIsActive(keys) {
  const ks = sortReframeKeys(keys);
  if (ks.length >= 2) return true;
  if (ks.length === 1) return !!normalizeCrop(ks[0].rect);
  return false;
}

// Linear-interpolated window {x,y,w,h} at time t. Holds the first/last key
// outside the animated span (so the look is steady at head/tail), mirroring the
// grade keyframe contract. Returns null when there are no usable keys.
export function reframeAtTime(keys, t) {
  const ks = sortReframeKeys(keys);
  if (!ks.length) return null;
  if (ks.length === 1 || t <= ks[0].t) return { ...ks[0].rect };
  const last = ks[ks.length - 1];
  if (t >= last.t) return { ...last.rect };
  let i = 0;
  while (i < ks.length - 1 && ks[i + 1].t <= t) i += 1;
  const a = ks[i].rect;
  const b = ks[i + 1].rect;
  const span = ks[i + 1].t - ks[i].t;
  const f = span > 1e-6 ? (t - ks[i].t) / span : 0;
  return {
    x: a.x + (b.x - a.x) * f,
    y: a.y + (b.y - a.y) * f,
    w: a.w + (b.w - a.w) * f,
    h: a.h + (b.h - a.h) * f,
  };
}

// The widest window the animation reaches — its pixel size is the render's
// output resolution (tighter frames upscale to fill it). Aspect is locked to
// key[0], so max-w and max-h come from the same key.
export function reframeMaxRect(keys) {
  const ks = sortReframeKeys(keys);
  if (!ks.length) return null;
  let best = ks[0].rect;
  for (const k of ks) if (k.rect.w * k.rect.h > best.w * best.h) best = k.rect;
  return { ...best };
}
